import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronDown, ChevronRight, Plus, Trash2, 
  Edit2, Users, AlertTriangle, CheckCircle, Clock,
  TrendingUp, DollarSign, Activity, Lock, ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Layout from '@/components/Layout';
import ProjectTeamCard from '@/components/ProjectTeamCard';
import { fh } from '@/utils/hours';
import { api, toast } from '../App';

const ProjectPlanning = ({ user }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  // State
  const [project, setProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [team, setTeam] = useState([]);
  
  // Activity type → required role mapping (for filtering assignee dropdowns)
  const ACTIVITY_PRODUCER_ROLE = {
    modeling: "modeller",
    detailing: "detailer",
    erection: "erector",
    planning: "coordinator",
  };
  
  // Build team-filtered user list based on the activity's required role.
  //   - For checking activities (activity_type ends in "_checking"), the required role is "checker".
  //   - For producer activities (modeling/detailing/erection), the required role is the matching producer role.
  //   - Caller can override via isCheckerRole=true (used by the "Checker" sub-dropdown in Add-Task).
  //   - Legacy projects with no team fall back to the full user list.
  const getEligibleAssignees = (activityType, isCheckerRole = false) => {
    if (!team || team.length === 0) return users;
    const at = (activityType || "").toLowerCase();
    const isCheckingActivity = at.includes("checking");
    const baseType = isCheckingActivity ? at.replace("_checking", "").replace("checking", "") : at;
    const requiredRole = (isCheckerRole || isCheckingActivity) ? "checker" : ACTIVITY_PRODUCER_ROLE[baseType];
    if (!requiredRole) {
      // Unknown / non-pair activity → any team member
      const teamUserIds = team.map(m => m.user_id);
      return users.filter(u => teamUserIds.includes(u.id));
    }
    const teamUserIds = team.filter(m => (m.roles || []).includes(requiredRole)).map(m => m.user_id);
    return users.filter(u => teamUserIds.includes(u.id));
  };
  const [loading, setLoading] = useState(true);
  const [expandedPhases, setExpandedPhases] = useState({});
  const [expandedScopes, setExpandedScopes] = useState({});

  // Dialog states
  const [initializeDialog, setInitializeDialog] = useState(false);
  const [addScopeDialog, setAddScopeDialog] = useState(false);
  const [addActivityDialog, setAddActivityDialog] = useState(false);
  const [addTaskDialog, setAddTaskDialog] = useState(false);
  // Leave-conflict confirmation: stored when a 409 comes back so we can show details and let the planner retry with confirm_over_leave=true.
  const [leaveConflictDialog, setLeaveConflictDialog] = useState({ open: false, leaves: [], onConfirm: null });
  const [editScopeDialog, setEditScopeDialog] = useState(false);
  const [editActivityDialog, setEditActivityDialog] = useState(false);
  const [editTaskDialog, setEditTaskDialog] = useState(false);
  const [editPhaseDialog, setEditPhaseDialog] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: '', item: null });
  const [expandedActivities, setExpandedActivities] = useState({});

  // Form states
  const [approvalPercentage, setApprovalPercentage] = useState(70);
  const [fabricationPercentage, setFabricationPercentage] = useState(30);
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [selectedScope, setSelectedScope] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  // Phase-split edit dialog
  const [splitEditDialog, setSplitEditDialog] = useState(false);
  const [splitEditForm, setSplitEditForm] = useState({ approval: 70, fabrication: 30 });
  const [splitEditSaving, setSplitEditSaving] = useState(false);
  
  // Task form states
  const [taskDescription, setTaskDescription] = useState('');
  const [taskHours, setTaskHours] = useState('');
  const [taskAssignedTo, setTaskAssignedTo] = useState('');
  const [taskPlannedStartDate, setTaskPlannedStartDate] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskStatus, setTaskStatus] = useState('todo');
  
  // Producer-Checker pairing state
  const [autoPairChecker, setAutoPairChecker] = useState(true);
  const [taskCheckerAssignedTo, setTaskCheckerAssignedTo] = useState('');
  const [taskCheckerHours, setTaskCheckerHours] = useState('');
  
  // Edit form states
  const [editHours, setEditHours] = useState('');
  const [editScopeName, setEditScopeName] = useState('');

  // Change Order activity dialog state
  const [addCoActivityDialog, setAddCoActivityDialog] = useState(false);
  const [selectedChangeOrder, setSelectedChangeOrder] = useState(null);
  const [expandedChangeOrders, setExpandedChangeOrders] = useState({});
  const [coActivityType, setCoActivityType] = useState('');
  const [coActivityHours, setCoActivityHours] = useState('');

  // Fetch data functions
  const fetchProject = async () => {
    try {
      const response = await api.get(`/projects/${projectId}`);
      setProject(response.data);
    } catch (error) {
      console.error('Error fetching project:', error);
      toast.error('Failed to load project details');
    }
  };

  const fetchPhases = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/planning/phases`);
      setPhases(response.data.phases || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching phases:', error);
      setLoading(false);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await api.get(`/projects/${projectId}/planning/activities`);
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        // Use Promise.allSettled so a single failing call (e.g., /users for
        // users without Users-access) doesn't blank out the whole page.
        const results = await Promise.allSettled([
          api.get(`/projects/${projectId}`),
          api.get(`/projects/${projectId}/planning/phases`),
          api.get(`/projects/${projectId}/planning/activities`),
          api.get('/users')
        ]);
        const [projectRes, phasesRes, activitiesRes, usersRes] = results;
        if (projectRes.status === 'fulfilled') setProject(projectRes.value.data);
        if (phasesRes.status === 'fulfilled') setPhases(phasesRes.value.data.phases || []);
        if (activitiesRes.status === 'fulfilled') setActivities(activitiesRes.value.data.activities || []);
        if (usersRes.status === 'fulfilled') setUsers(usersRes.value.data);
        // Log failures (silently — these are non-critical for view-only users)
        results.forEach((r, i) => {
          if (r.status === 'rejected') {
            console.warn(`Planning data call ${i} failed:`, r.reason?.response?.data?.detail || r.reason?.message);
          }
        });
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [projectId]);

  // Reload all data helper
  const reloadData = async () => {
    const results = await Promise.allSettled([
      api.get(`/projects/${projectId}/planning/phases`),
      api.get(`/projects/${projectId}/planning/activities`)
    ]);
    if (results[0].status === 'fulfilled') setPhases(results[0].value.data.phases || []);
    if (results[1].status === 'fulfilled') setActivities(results[1].value.data.activities || []);
  };

  // Delete handler
  const handleDelete = async () => {
    const { type, item } = deleteTarget;
    try {
      switch (type) {
        case 'phase':
          await api.delete(`/planning/phases/${item.id}`);
          break;
        case 'scope':
          await api.delete(`/planning/scopes/${item.id}?phase_id=${selectedPhase.id}`);
          break;
        case 'activity':
          await api.delete(`/planning/activities/${item.id}`);
          break;
        case 'task':
          await api.delete(`/planning/tasks/${item.id}?activity_id=${selectedActivity.id}`);
          break;
        default:
          throw new Error('Unknown delete type');
      }
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted successfully`);
      setDeleteConfirmDialog(false);
      reloadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to delete ${type}`);
    }
  };

  // Initialize planning
  const handleInitializePlanning = async () => {
    try {
      const response = await api.post(
        `/projects/${projectId}/planning/initialize?approval_percentage=${approvalPercentage}&fabrication_percentage=${fabricationPercentage}`
      );

      if (response.status === 200) {
        toast.success('Planning initialized successfully');
        setInitializeDialog(false);
        // Reload the data
        const [phasesRes, activitiesRes] = await Promise.all([
          api.get(`/projects/${projectId}/planning/phases`),
          api.get(`/projects/${projectId}/planning/activities`)
        ]);
        setPhases(phasesRes.data.phases || []);
        setActivities(activitiesRes.data.activities || []);
      }
    } catch (error) {
      console.error('Error initializing planning:', error);
      toast.error(error.response?.data?.detail || 'Failed to initialize planning');
    }
  };

  const handleSavePhaseSplit = async () => {
    const a = parseFloat(splitEditForm.approval);
    const f = parseFloat(splitEditForm.fabrication);
    if (isNaN(a) || isNaN(f)) {
      toast.error('Enter both percentages');
      return;
    }
    if (Math.round((a + f) * 100) / 100 !== 100) {
      toast.error(`Percentages must sum to 100 (got ${(a + f).toFixed(2)})`);
      return;
    }
    setSplitEditSaving(true);
    try {
      await api.put(`/projects/${projectId}/planning/phase-split`, {
        approval_percentage: a,
        fabrication_percentage: f,
      });
      toast.success(`Split updated to ${a}% / ${f}%`);
      setSplitEditDialog(false);
      const phasesRes = await api.get(`/projects/${projectId}/planning/phases`);
      setPhases(phasesRes.data.phases || []);
      // Refresh the project doc so the banner reflects new percentages
      const projRes = await api.get(`/projects/${projectId}`);
      setProject(projRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update split');
    } finally {
      setSplitEditSaving(false);
    }
  };

  // Toggle expand/collapse
  const togglePhase = (phaseId) => {
    setExpandedPhases(prev => ({ ...prev, [phaseId]: !prev[phaseId] }));
  };

  const toggleScope = (scopeId) => {
    setExpandedScopes(prev => ({ ...prev, [scopeId]: !prev[scopeId] }));
  };

  const toggleActivity = (activityId) => {
    setExpandedActivities(prev => ({ ...prev, [activityId]: !prev[activityId] }));
  };

  // Get activities for a scope
  const getActivitiesForScope = (scopeId) => {
    return activities.filter(a => a.scope_allocation_id === scopeId);
  };

  // Get activities for change order
  const getChangeOrderActivities = () => {
    return activities.filter(a => a.activity_context === 'change_order');
  };

  // Activities for a specific change order
  const getActivitiesForChangeOrder = (changeOrderId) => {
    return activities.filter(a => a.change_order_id === changeOrderId);
  };

  // All approved change orders
  const getApprovedChangeOrders = () => {
    return (project?.change_orders || []).filter(co => co.status === 'approved');
  };

  // Hours of a CO (approved_hours fallback to time_impact)
  const getCoHours = (co) => co?.approved_hours || co?.time_impact || 0;

  // Allocated hours under a CO
  const getCoAllocatedHours = (coId) => {
    return getActivitiesForChangeOrder(coId).reduce((s, a) => s + (a.allocated_hours || 0), 0);
  };

  const toggleChangeOrder = (coId) => {
    setExpandedChangeOrders(prev => ({ ...prev, [coId]: !prev[coId] }));
  };

  // Round helper — all hours displayed as integers
  // Round to nearest 0.5 — delegates to shared util so display is consistent across the app.
  const roundHrs = fh;

  // Sum of approved change order hours from project.change_orders
  const getApprovedChangeOrderHours = () => {
    return (project?.change_orders || [])
      .filter(co => co.status === 'approved')
      .reduce((sum, co) => sum + (co.approved_hours || co.time_impact || 0), 0);
  };

  // Total project allocated hours including approved change orders
  const getTotalAllocatedHours = () => {
    return roundHrs((project?.allocated_hours || 0) + getApprovedChangeOrderHours());
  };

  // Split phases so the two values add up exactly to project.allocated_hours
  // (avoids 60+40 looking like 61+40 due to independent rounding)
  const getSplitHours = (percentage, isLast = false) => {
    const total = project?.allocated_hours || 0;
    if (isLast) {
      const other = Math.round((total * (100 - percentage)) / 100);
      return roundHrs(total - other);
    }
    return Math.round((total * percentage) / 100);
  };

  // Calculate variance color
  const getVarianceColor = (percentage) => {
    const abs = Math.abs(percentage);
    if (abs > 20) return 'text-md3-error';
    if (abs > 10) return 'text-md3-tertiary';
    return 'text-md3-primary';
  };

  // RBAC Permission Check
  const permissions = user?.permissions?.projectPlanning || {};
  const canView = permissions?.view ?? true;
  const canEdit = permissions?.edit ?? false;
  const canCreate = permissions?.create ?? false;

  // Check if planning is locked
  const isPlanningLocked = project?.planning_locked ?? false;
  const isManagement = user?.department === 'Management' || user?.access_level === 'Admin';
  // Projects Manager / Team Lead are allowed to edit project plans (add/edit scopes, activities, tasks, users)
  const isProjectsLead = user?.department === 'Projects' && ['Manager', 'Team Lead'].includes(user?.access_level);
  const canModify = canEdit && (!isPlanningLocked || isManagement || isProjectsLead);

  // Main render with planning data
  const approvalPhase = phases.find(p => p.phase_type === 'approval');
  const fabricationPhase = phases.find(p => p.phase_type === 'fabrication');

  return (
    <Layout user={user}>
      {loading ? (
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-md3-surface-container-high rounded-md3-md w-1/4 mb-4"></div>
            <div className="h-64 bg-md3-surface-container-high rounded-md3-lg"></div>
          </div>
        </div>
      ) : phases.length === 0 ? (
        <div className="p-6">
          <Card className="bg-md3-surface-container border-0 shadow-md3-1 rounded-md3-lg">
            <CardHeader>
              <CardTitle className="text-md3-title-large text-md3-on-surface">Project Planning</CardTitle>
            </CardHeader>
            <CardContent className="text-center py-12">
              <div className="h-16 w-16 mx-auto mb-4 rounded-md3-full bg-md3-surface-container-high flex items-center justify-center">
                <Activity className="h-8 w-8 text-md3-on-surface-variant" />
              </div>
              <h3 className="text-md3-title-medium text-md3-on-surface mb-2">No Planning Initialized</h3>
              <p className="text-md3-body-medium text-md3-on-surface-variant mb-6">
                Initialize project planning to start allocating hours and tracking resources
              </p>
              {canCreate && (
                <Button 
                  onClick={() => setInitializeDialog(true)}
                  className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full shadow-md3-1"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Initialize Planning
                </Button>
              )}
              {!canCreate && (
                <p className="text-md3-body-small text-md3-error">
                  You do not have permission to initialize planning. Contact your administrator.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Initialize Dialog */}
          <Dialog open={initializeDialog} onOpenChange={setInitializeDialog}>
            <DialogContent className="bg-md3-surface-container-high rounded-md3-xl">
              <DialogHeader>
                <DialogTitle className="text-md3-title-large text-md3-on-surface">Initialize Project Planning</DialogTitle>
                <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
                  Set up phase distribution for this project. Total must equal 100%.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Approval Phase (%)</Label>
                  <Input
                    type="number"
                    value={approvalPercentage}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      setApprovalPercentage(val);
                      setFabricationPercentage(100 - val);
                    }}
                    min="0"
                    max="100"
                    className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                  />
                  <p className="text-md3-body-small text-md3-on-surface-variant mt-1">
                    {project?.allocated_hours ? 
                      `${getSplitHours(approvalPercentage)} hours` 
                      : ''}
                  </p>
                </div>
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Fabrication Phase (%)</Label>
                  <Input
                    type="number"
                    value={fabricationPercentage}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      setFabricationPercentage(val);
                      setApprovalPercentage(100 - val);
                    }}
                    min="0"
                    max="100"
                    className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                  />
                  <p className="text-md3-body-small text-md3-on-surface-variant mt-1">
                    {project?.allocated_hours ? 
                      `${getSplitHours(fabricationPercentage, true)} hours` 
                      : ''}
                  </p>
                </div>
                {(approvalPercentage + fabricationPercentage) !== 100 && (
                  <p className="text-md3-body-small text-md3-error">
                    Total must equal 100% (currently {approvalPercentage + fabricationPercentage}%)
                  </p>
                )}
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setInitializeDialog(false)}
                  className="border-md3-outline text-md3-primary rounded-md3-full"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleInitializePlanning}
                  disabled={(approvalPercentage + fabricationPercentage) !== 100}
                  className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
                >
                  Initialize
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      ) : (
        <div className="p-6 space-y-6">
          {/* Phase Split Edit Dialog */}
          <Dialog open={splitEditDialog} onOpenChange={(o) => !splitEditSaving && setSplitEditDialog(o)}>
            <DialogContent className="bg-md3-surface rounded-md3-lg max-w-md" data-testid="planning-phase-split-dialog">
              <DialogHeader>
                <DialogTitle className="text-md3-title-large text-md3-on-surface">Edit Phase Split</DialogTitle>
                <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
                  Adjust the Approval / Fabrication hour distribution. Locked once earned hours reach the phase allocation.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-3">
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Approval Phase (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={splitEditForm.approval}
                    onChange={(e) => {
                      const a = parseFloat(e.target.value);
                      const safe = isNaN(a) ? 0 : a;
                      setSplitEditForm({ approval: e.target.value, fabrication: Math.round((100 - safe) * 100) / 100 });
                    }}
                    className="mt-1"
                    data-testid="planning-split-approval-input"
                  />
                </div>
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Fabrication Phase (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={splitEditForm.fabrication}
                    onChange={(e) => {
                      const f = parseFloat(e.target.value);
                      const safe = isNaN(f) ? 0 : f;
                      setSplitEditForm({ fabrication: e.target.value, approval: Math.round((100 - safe) * 100) / 100 });
                    }}
                    className="mt-1"
                    data-testid="planning-split-fabrication-input"
                  />
                </div>
                {(() => {
                  const total = (parseFloat(splitEditForm.approval) || 0) + (parseFloat(splitEditForm.fabrication) || 0);
                  if (Math.round(total * 100) / 100 !== 100) {
                    return (
                      <p className="text-md3-body-small text-md3-error">
                        Total must equal 100% (currently {total.toFixed(2)}%)
                      </p>
                    );
                  }
                  return (
                    <p className="text-md3-body-small text-md3-on-surface-variant">
                      Preview at {roundHrs(project?.allocated_hours || 0)}h total:
                      Approval → {roundHrs((project?.allocated_hours || 0) * (parseFloat(splitEditForm.approval) / 100))}h ·
                      Fabrication → {roundHrs((project?.allocated_hours || 0) * (parseFloat(splitEditForm.fabrication) / 100))}h
                    </p>
                  );
                })()}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setSplitEditDialog(false)}
                  disabled={splitEditSaving}
                  className="border-md3-outline text-md3-primary rounded-md3-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSavePhaseSplit}
                  disabled={splitEditSaving}
                  className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
                  data-testid="planning-phase-split-save-btn"
                >
                  {splitEditSaving ? "Saving..." : "Save Split"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Header - MD3 Style */}
          <div className="flex items-center justify-between">
            <div>
              <Button 
                variant="ghost" 
                onClick={() => navigate(`/projects/${projectId}`)}
                className="mb-2 text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Project
              </Button>
              <div className="flex items-center gap-3">
                <h1 className="text-md3-display-small text-md3-on-surface">
                  {project?.project_number} - Planning
                </h1>
                {isPlanningLocked && (
                  <Badge className="bg-md3-tertiary-container text-md3-on-tertiary-container">
                    <Lock className="h-3 w-3 mr-1" />
                    Locked
                  </Badge>
                )}
              </div>
              <p className="text-md3-body-medium text-md3-on-surface-variant">{project?.name}</p>
            </div>
            <div className="flex gap-2 items-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/planning-board/${projectId}`)}
                className="border-md3-primary text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                data-testid="open-planning-board-btn"
              >
                Planning Board (Gantt)
              </Button>
            </div>
            {isPlanningLocked && !isManagement && !isProjectsLead && (
              <div className="text-md3-body-small text-md3-error bg-md3-error-container px-4 py-2 rounded-md3-lg">
                Planning is locked. Contact your Projects Manager or Management to make changes.
              </div>
            )}
            {isPlanningLocked && (isManagement || isProjectsLead) && (
              <div className="text-md3-body-small text-md3-primary bg-md3-primary-container px-4 py-2 rounded-md3-lg">
                Planning is locked but you can edit as {isManagement ? 'Management/Admin' : 'Projects ' + (user?.access_level || 'Lead')}.
              </div>
            )}
          </div>

          {/* Summary Cards - MD3 Style */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-md3-label-medium text-md3-on-surface-variant">Total Hours</p>
                    <p className="text-md3-display-small text-md3-on-surface" data-testid="planning-total-hours">{getTotalAllocatedHours()}</p>
                    {getApprovedChangeOrderHours() > 0 && (
                      <p className="text-md3-body-small text-md3-on-surface-variant">
                        Base {roundHrs(project?.allocated_hours)} + CO {roundHrs(getApprovedChangeOrderHours())}
                      </p>
                    )}
                  </div>
                  <div className="h-12 w-12 rounded-md3-full bg-md3-primary-container flex items-center justify-center">
                    <Clock className="h-6 w-6 text-md3-on-primary-container" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-md3-label-medium text-md3-on-surface-variant">Approval</p>
                    <p className="text-md3-display-small text-md3-on-surface">{roundHrs(approvalPhase?.allocated_hours)}</p>
                    <p className="text-md3-body-small text-md3-on-surface-variant">
                      {approvalPhase?.completion_percentage?.toFixed(0) || 0}% complete
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-md3-full bg-md3-tertiary-container flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-md3-on-tertiary-container" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-md3-label-medium text-md3-on-surface-variant">Fabrication</p>
                    <p className="text-md3-display-small text-md3-on-surface">{roundHrs(fabricationPhase?.allocated_hours)}</p>
                    <p className="text-md3-body-small text-md3-on-surface-variant">
                      {fabricationPhase?.completion_percentage?.toFixed(0) || 0}% complete
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-md3-full bg-md3-secondary-container flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-md3-on-secondary-container" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-md3-label-medium text-md3-on-surface-variant">Change Orders</p>
                    <p className="text-md3-display-small text-md3-on-surface" data-testid="planning-co-hours">{roundHrs(getApprovedChangeOrderHours())}<span className="text-md3-body-medium text-md3-on-surface-variant"> hrs</span></p>
                    <p className="text-md3-body-small text-md3-on-surface-variant">
                      {(project?.change_orders || []).filter(co => co.status === 'approved').length} approved · {getChangeOrderActivities().length} planned
                    </p>
                  </div>
                  <div className="h-12 w-12 rounded-md3-full bg-md3-error-container flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-md3-on-error-container" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Project Team Card */}
          <ProjectTeamCard
            projectId={projectId}
            allUsers={users}
            canManage={
              isManagement
              || (user?.access_level === 'Admin')
              || (user?.department === 'Projects' && ['Manager', 'Team Lead'].includes(user?.access_level))
              || (team || []).some(m => m.user_id === user?.id && (m.roles || []).includes('lead'))
            }
            onTeamChange={(t) => setTeam(t || [])}
          />

          {/* Phase Split Banner + Edit */}
          {approvalPhase && fabricationPhase && (
            <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-6">
                    <div>
                      <p className="text-md3-label-small text-md3-on-surface-variant">Phase Split</p>
                      <p className="text-md3-title-medium text-md3-on-surface font-semibold">
                        Approval {project?.approval_phase_percentage ?? 70}% · Fabrication {project?.fabrication_phase_percentage ?? 30}%
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-md3-body-small">
                      <div className="text-md3-on-surface-variant">
                        <span className="font-medium text-md3-on-surface">Approval</span> {roundHrs(approvalPhase.actual_hours)}h earned / {roundHrs(approvalPhase.allocated_hours)}h
                      </div>
                      <div className="text-md3-on-surface-variant">
                        <span className="font-medium text-md3-on-surface">Fabrication</span> {roundHrs(fabricationPhase.actual_hours)}h earned / {roundHrs(fabricationPhase.allocated_hours)}h
                      </div>
                    </div>
                  </div>
                  {canModify && (() => {
                    const aLocked = (approvalPhase.allocated_hours || 0) > 0 && (approvalPhase.actual_hours || 0) >= (approvalPhase.allocated_hours || 0);
                    const fLocked = (fabricationPhase.allocated_hours || 0) > 0 && (fabricationPhase.actual_hours || 0) >= (fabricationPhase.allocated_hours || 0);
                    const isLocked = aLocked || fLocked;
                    return (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isLocked}
                        onClick={() => {
                          setSplitEditForm({
                            approval: project?.approval_phase_percentage ?? 70,
                            fabrication: project?.fabrication_phase_percentage ?? 30,
                          });
                          setSplitEditDialog(true);
                        }}
                        className="rounded-md3-full"
                        title={isLocked ? "A phase has reached its allocation limit — split is locked" : "Edit approval / fabrication phase split"}
                        data-testid="planning-phase-split-edit-btn"
                      >
                        <Edit2 className="h-4 w-4 mr-1" />
                        {isLocked ? "Split locked" : "Edit Split"}
                      </Button>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Approval Phase - MD3 Style */}
          {approvalPhase && (
            <Card className="bg-md3-surface-container border-0 shadow-md3-1 rounded-md3-lg">
              <CardHeader 
                className="cursor-pointer hover:bg-md3-surface-container-high rounded-t-md3-lg transition-colors"
                onClick={() => togglePhase(approvalPhase.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {expandedPhases[approvalPhase.id] ? 
                      <ChevronDown className="h-5 w-5 text-md3-on-surface" /> : 
                      <ChevronRight className="h-5 w-5 text-md3-on-surface" />
                    }
                    <CardTitle className="text-md3-title-large text-md3-on-surface">
                      APPROVAL PHASE: {roundHrs(approvalPhase.allocated_hours)}hrs
                    </CardTitle>
                    <Badge className="bg-md3-secondary-container text-md3-on-secondary-container">
                      {approvalPhase.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-md3-body-medium text-md3-on-surface-variant">
                      {roundHrs(approvalPhase.actual_hours)} / {roundHrs(approvalPhase.allocated_hours)} hrs
                    </span>
                    <Progress 
                      value={approvalPhase.completion_percentage || 0} 
                      className="w-24"
                    />
                    {canModify && (
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPhase(approvalPhase);
                          setAddScopeDialog(true);
                        }}
                        className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Scope
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              {expandedPhases[approvalPhase.id] && (
                <CardContent className="space-y-4">
                  {approvalPhase.scope_allocations?.map(scope => (
                    <div key={scope.id} className="border border-md3-outline-variant rounded-md3-lg p-4 bg-md3-surface">
                      <div 
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleScope(scope.id)}
                      >
                        <div className="flex items-center gap-2">
                          {expandedScopes[scope.id] ? 
                            <ChevronDown className="h-4 w-4 text-md3-on-surface" /> : 
                            <ChevronRight className="h-4 w-4 text-md3-on-surface" />
                          }
                          <h4 className="text-md3-title-medium text-md3-on-surface">
                            {scope.scope_name}: {roundHrs(scope.allocated_hours)}hrs
                          </h4>
                          {scope.variance_hours && (
                            <Badge variant="outline" className={`border-md3-outline ${getVarianceColor(scope.actual_variance_percentage)}`}>
                              {scope.variance_hours > 0 ? '+' : ''}{roundHrs(scope.variance_hours)}hrs
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {canModify && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedPhase(approvalPhase);
                                  setSelectedScope(scope);
                                  setEditHours(scope.allocated_hours?.toString() || '');
                                  setEditScopeName(scope.scope_name || '');
                                  setEditScopeDialog(true);
                                }}
                                className="text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedPhase(approvalPhase);
                                  setSelectedScope(scope);
                                  setDeleteTarget({ type: 'scope', item: scope });
                                  setDeleteConfirmDialog(true);
                                }}
                                className="text-md3-error hover:bg-md3-error-container rounded-md3-full"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedPhase(approvalPhase);
                                  setSelectedScope(scope);
                                  setAddActivityDialog(true);
                                }}
                                className="border-md3-outline text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Add Activity
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {expandedScopes[scope.id] && (
                        <div className="mt-4 space-y-2 ml-6">
                          {getActivitiesForScope(scope.id).map(activity => (
                            <div 
                              key={activity.id}
                              className="border border-md3-outline-variant rounded-md3-md bg-md3-surface-container-high"
                            >
                              {/* Activity Header */}
                              <div 
                                className="flex items-center justify-between p-3 cursor-pointer hover:bg-md3-surface-container"
                                onClick={() => toggleActivity(activity.id)}
                              >
                                <div className="flex items-center gap-2">
                                  {expandedActivities[activity.id] ? 
                                    <ChevronDown className="h-4 w-4 text-md3-on-surface" /> : 
                                    <ChevronRight className="h-4 w-4 text-md3-on-surface" />
                                  }
                                  <Badge className="bg-md3-secondary-container text-md3-on-secondary-container">
                                    {activity.activity_type?.replace(/_/g, ' ')}
                                  </Badge>
                                  {activity.auto_created && (
                                    <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-[10px] gap-1" title="This checking activity was auto-created by the producer-checker workflow">
                                      🔗 Auto
                                    </Badge>
                                  )}
                                  <span className="text-md3-body-medium text-md3-on-surface font-medium">
                                    {roundHrs(activity.allocated_hours)}hrs
                                  </span>
                                  {activity.has_variance_warning && (
                                    <AlertTriangle className="h-4 w-4 text-md3-error" />
                                  )}
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-md3-body-small text-md3-on-surface-variant">
                                    {activity.tasks?.length || 0} tasks
                                  </span>
                                  <Progress 
                                    value={activity.completion_percentage || 0} 
                                    className="w-20"
                                  />
                                  {canModify && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedActivity(activity);
                                          setEditHours(activity.allocated_hours?.toString() || '');
                                          setEditActivityDialog(true);
                                        }}
                                        className="text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                                      >
                                        <Edit2 className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedActivity(activity);
                                          setDeleteTarget({ type: 'activity', item: activity });
                                          setDeleteConfirmDialog(true);
                                        }}
                                        className="text-md3-error hover:bg-md3-error-container rounded-md3-full"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedActivity(activity);
                                          setTaskDescription('');
                                          setTaskHours('');
                                          setTaskAssignedTo('');
                                          setTaskPlannedStartDate('');
                                          setTaskDueDate('');
                                          setAddTaskDialog(true);
                                        }}
                                        className="border-md3-outline text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                                      >
                                        <Plus className="h-4 w-4 mr-1" />
                                        Add Task
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                              
                              {/* Tasks inside Activity */}
                              {expandedActivities[activity.id] && (
                                <div className="border-t border-md3-outline-variant p-3 space-y-2 bg-md3-surface">
                                  {activity.tasks?.length > 0 ? (
                                    activity.tasks.map(task => (
                                      <div 
                                        key={task.id}
                                        className="flex items-center justify-between p-2 bg-md3-surface-container rounded-md3-sm"
                                      >
                                        <div className="flex-1">
                                          <p className="text-md3-body-medium text-md3-on-surface">{task.description}</p>
                                          <div className="flex items-center gap-3 mt-1 text-md3-body-small text-md3-on-surface-variant">
                                            <span>{roundHrs(task.allocated_hours)}hrs</span>
                                            {task.assigned_user_name && (
                                              <span className="flex items-center gap-1">
                                                <Users className="h-3 w-3" />
                                                {task.assigned_user_name}
                                              </span>
                                            )}
                                            {task.due_date && (
                                              <span className="flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {task.due_date}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Badge 
                                            className={
                                              task.status === 'done' ? 'bg-md3-tertiary-container text-md3-on-tertiary-container' :
                                              task.status === 'in_progress' ? 'bg-md3-primary-container text-md3-on-primary-container' :
                                              'bg-md3-surface-container-high text-md3-on-surface-variant'
                                            }
                                          >
                                            {task.status?.replace(/_/g, ' ') || 'todo'}
                                          </Badge>
                                          {canModify && (
                                            <>
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => {
                                                  setSelectedActivity(activity);
                                                  setSelectedTask(task);
                                                  setTaskDescription(task.description || '');
                                                  setTaskHours(task.allocated_hours?.toString() || '');
                                                  setTaskAssignedTo(task.assigned_to || 'unassigned');
                                                  setTaskPlannedStartDate(task.planned_start_date || '');
                                                  setTaskDueDate(task.due_date || '');
                                                  setTaskStatus(task.status || 'todo');
                                                  setEditTaskDialog(true);
                                                }}
                                                className="h-7 w-7 p-0 text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                                              >
                                                <Edit2 className="h-3 w-3" />
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => {
                                                  setSelectedActivity(activity);
                                                  setDeleteTarget({ type: 'task', item: task });
                                                  setDeleteConfirmDialog(true);
                                                }}
                                                className="h-7 w-7 p-0 text-md3-error hover:bg-md3-error-container rounded-md3-full"
                                              >
                                                <Trash2 className="h-3 w-3" />
                                              </Button>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <p className="text-md3-body-small text-md3-on-surface-variant italic text-center py-2">
                                      No tasks yet. Click &quot;Add Task&quot; to create one.
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                          {getActivitiesForScope(scope.id).length === 0 && (
                            <p className="text-md3-body-small text-md3-on-surface-variant italic">No activities yet</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          )}

          {/* Fabrication Phase - MD3 Style */}
          {fabricationPhase && (
            <Card className="bg-md3-surface-container border-0 shadow-md3-1 rounded-md3-lg">
              <CardHeader 
                className="cursor-pointer hover:bg-md3-surface-container-high rounded-t-md3-lg transition-colors"
                onClick={() => togglePhase(fabricationPhase.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {expandedPhases[fabricationPhase.id] ? 
                      <ChevronDown className="h-5 w-5 text-md3-on-surface" /> : 
                      <ChevronRight className="h-5 w-5 text-md3-on-surface" />
                    }
                    <CardTitle className="text-md3-title-large text-md3-on-surface">
                      FABRICATION PHASE: {roundHrs(fabricationPhase.allocated_hours)}hrs
                    </CardTitle>
                    <Badge className="bg-md3-secondary-container text-md3-on-secondary-container">
                      {fabricationPhase.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-md3-body-medium text-md3-on-surface-variant">
                      {roundHrs(fabricationPhase.actual_hours)} / {roundHrs(fabricationPhase.allocated_hours)} hrs
                    </span>
                    <Progress 
                      value={fabricationPhase.completion_percentage || 0} 
                      className="w-24"
                    />
                    {canModify && (
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPhase(fabricationPhase);
                          setAddScopeDialog(true);
                        }}
                        className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Scope
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              {expandedPhases[fabricationPhase.id] && (
                <CardContent className="space-y-4">
                  {fabricationPhase.scope_allocations?.map(scope => (
                    <div key={scope.id} className="border border-md3-outline-variant rounded-md3-lg p-4 bg-md3-surface">
                      <div 
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleScope(scope.id)}
                      >
                        <div className="flex items-center gap-2">
                          {expandedScopes[scope.id] ? 
                            <ChevronDown className="h-4 w-4 text-md3-on-surface" /> : 
                            <ChevronRight className="h-4 w-4 text-md3-on-surface" />
                          }
                          <h4 className="text-md3-title-medium text-md3-on-surface">
                            {scope.scope_name}: {roundHrs(scope.allocated_hours)}hrs
                          </h4>
                          {scope.variance_hours && (
                            <Badge variant="outline" className={`border-md3-outline ${getVarianceColor(scope.actual_variance_percentage)}`}>
                              {scope.variance_hours > 0 ? '+' : ''}{roundHrs(scope.variance_hours)}hrs
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {canModify && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedPhase(fabricationPhase);
                                  setSelectedScope(scope);
                                  setEditHours(scope.allocated_hours?.toString() || '');
                                  setEditScopeName(scope.scope_name || '');
                                  setEditScopeDialog(true);
                                }}
                                className="text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedPhase(fabricationPhase);
                                  setSelectedScope(scope);
                                  setDeleteTarget({ type: 'scope', item: scope });
                                  setDeleteConfirmDialog(true);
                                }}
                                className="text-md3-error hover:bg-md3-error-container rounded-md3-full"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedPhase(fabricationPhase);
                                  setSelectedScope(scope);
                                  setAddActivityDialog(true);
                                }}
                                className="border-md3-outline text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Add Activity
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {expandedScopes[scope.id] && (
                        <div className="mt-4 space-y-2 ml-6">
                          {getActivitiesForScope(scope.id).map(activity => (
                            <div 
                              key={activity.id}
                              className="border border-md3-outline-variant rounded-md3-md bg-md3-surface-container-high"
                            >
                              {/* Activity Header */}
                              <div 
                                className="flex items-center justify-between p-3 cursor-pointer hover:bg-md3-surface-container"
                                onClick={() => toggleActivity(activity.id)}
                              >
                                <div className="flex items-center gap-2">
                                  {expandedActivities[activity.id] ? 
                                    <ChevronDown className="h-4 w-4 text-md3-on-surface" /> : 
                                    <ChevronRight className="h-4 w-4 text-md3-on-surface" />
                                  }
                                  <Badge className="bg-md3-secondary-container text-md3-on-secondary-container">
                                    {activity.activity_type?.replace(/_/g, ' ')}
                                  </Badge>
                                  {activity.auto_created && (
                                    <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-[10px] gap-1" title="This checking activity was auto-created by the producer-checker workflow">
                                      🔗 Auto
                                    </Badge>
                                  )}
                                  <span className="text-md3-body-medium text-md3-on-surface font-medium">
                                    {roundHrs(activity.allocated_hours)}hrs
                                  </span>
                                  {activity.has_variance_warning && (
                                    <AlertTriangle className="h-4 w-4 text-md3-error" />
                                  )}
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-md3-body-small text-md3-on-surface-variant">
                                    {activity.tasks?.length || 0} tasks
                                  </span>
                                  <Progress 
                                    value={activity.completion_percentage || 0} 
                                    className="w-20"
                                  />
                                  {canModify && (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedActivity(activity);
                                          setEditHours(activity.allocated_hours?.toString() || '');
                                          setEditActivityDialog(true);
                                        }}
                                        className="text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                                      >
                                        <Edit2 className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedActivity(activity);
                                          setDeleteTarget({ type: 'activity', item: activity });
                                          setDeleteConfirmDialog(true);
                                        }}
                                        className="text-md3-error hover:bg-md3-error-container rounded-md3-full"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedActivity(activity);
                                          setTaskDescription('');
                                          setTaskHours('');
                                          setTaskAssignedTo('');
                                          setTaskPlannedStartDate('');
                                          setTaskDueDate('');
                                          setAddTaskDialog(true);
                                        }}
                                        className="border-md3-outline text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                                      >
                                        <Plus className="h-4 w-4 mr-1" />
                                        Add Task
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                              
                              {/* Tasks inside Activity */}
                              {expandedActivities[activity.id] && (
                                <div className="border-t border-md3-outline-variant p-3 space-y-2 bg-md3-surface">
                                  {activity.tasks?.length > 0 ? (
                                    activity.tasks.map(task => (
                                      <div 
                                        key={task.id}
                                        className="flex items-center justify-between p-2 bg-md3-surface-container rounded-md3-sm"
                                      >
                                        <div className="flex-1">
                                          <p className="text-md3-body-medium text-md3-on-surface">{task.description}</p>
                                          <div className="flex items-center gap-3 mt-1 text-md3-body-small text-md3-on-surface-variant">
                                            <span>{roundHrs(task.allocated_hours)}hrs</span>
                                            {task.assigned_user_name && (
                                              <span className="flex items-center gap-1">
                                                <Users className="h-3 w-3" />
                                                {task.assigned_user_name}
                                              </span>
                                            )}
                                            {task.due_date && (
                                              <span className="flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {task.due_date}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Badge 
                                            className={
                                              task.status === 'done' ? 'bg-md3-tertiary-container text-md3-on-tertiary-container' :
                                              task.status === 'in_progress' ? 'bg-md3-primary-container text-md3-on-primary-container' :
                                              'bg-md3-surface-container-high text-md3-on-surface-variant'
                                            }
                                          >
                                            {task.status?.replace(/_/g, ' ') || 'todo'}
                                          </Badge>
                                          {canModify && (
                                            <>
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => {
                                                  setSelectedActivity(activity);
                                                  setSelectedTask(task);
                                                  setTaskDescription(task.description || '');
                                                  setTaskHours(task.allocated_hours?.toString() || '');
                                                  setTaskAssignedTo(task.assigned_to || 'unassigned');
                                                  setTaskPlannedStartDate(task.planned_start_date || '');
                                                  setTaskDueDate(task.due_date || '');
                                                  setTaskStatus(task.status || 'todo');
                                                  setEditTaskDialog(true);
                                                }}
                                                className="h-7 w-7 p-0 text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                                              >
                                                <Edit2 className="h-3 w-3" />
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => {
                                                  setSelectedActivity(activity);
                                                  setDeleteTarget({ type: 'task', item: task });
                                                  setDeleteConfirmDialog(true);
                                                }}
                                                className="h-7 w-7 p-0 text-md3-error hover:bg-md3-error-container rounded-md3-full"
                                              >
                                                <Trash2 className="h-3 w-3" />
                                              </Button>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <p className="text-md3-body-small text-md3-on-surface-variant italic text-center py-2">
                                      No tasks yet. Click &quot;Add Task&quot; to create one.
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                          {getActivitiesForScope(scope.id).length === 0 && (
                            <p className="text-md3-body-small text-md3-on-surface-variant italic">No activities yet</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          )}

          {/* Approved Change Orders — plan activities under each CO */}
          {getApprovedChangeOrders().map(co => {
            const coId = co.id;
            const coHours = roundHrs(getCoHours(co));
            const coActivities = getActivitiesForChangeOrder(coId);
            const allocatedHours = roundHrs(getCoAllocatedHours(coId));
            const remaining = coHours - allocatedHours;
            return (
              <Card key={coId} className="bg-md3-surface-container border-0 shadow-md3-1 rounded-md3-lg" data-testid={`co-card-${coId}`}>
                <CardHeader
                  className="cursor-pointer hover:bg-md3-surface-container-high rounded-t-md3-lg transition-colors"
                  onClick={() => toggleChangeOrder(coId)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {expandedChangeOrders[coId] ?
                        <ChevronDown className="h-5 w-5 text-md3-on-surface" /> :
                        <ChevronRight className="h-5 w-5 text-md3-on-surface" />}
                      <CardTitle className="text-md3-title-large text-md3-on-surface">
                        CHANGE ORDER {co.change_order_number || co.co_number || ''}: {coHours}hrs
                      </CardTitle>
                      <Badge className="bg-md3-tertiary-container text-md3-on-tertiary-container">
                        approved
                      </Badge>
                      {co.description && (
                        <span className="text-md3-body-small text-md3-on-surface-variant ml-2 max-w-md truncate">
                          {co.description}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-md3-body-medium text-md3-on-surface-variant">
                        {allocatedHours} / {coHours} hrs
                      </span>
                      <Progress value={coHours > 0 ? (allocatedHours / coHours) * 100 : 0} className="w-24" />
                      {canModify && (
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedChangeOrder(co);
                            setCoActivityType('');
                            setCoActivityHours('');
                            setAddCoActivityDialog(true);
                          }}
                          className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
                          data-testid={`co-add-activity-${coId}`}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Activity
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>

                {expandedChangeOrders[coId] && (
                  <CardContent className="space-y-3">
                    {coActivities.length === 0 ? (
                      <p className="text-center text-md3-on-surface-variant py-6">
                        No activities planned for this Change Order yet. Click &quot;Add Activity&quot; to plan {coHours}hrs.
                      </p>
                    ) : (
                      <>
                        {coActivities.map(activity => (
                          <div
                            key={activity.id}
                            className="border border-md3-outline-variant rounded-md3-md bg-md3-surface-container-high"
                          >
                            {/* Activity Header */}
                            <div
                              className="flex items-center justify-between p-3 cursor-pointer hover:bg-md3-surface-container"
                              onClick={() => toggleActivity(activity.id)}
                            >
                              <div className="flex items-center gap-2">
                                {expandedActivities[activity.id] ?
                                  <ChevronDown className="h-4 w-4 text-md3-on-surface" /> :
                                  <ChevronRight className="h-4 w-4 text-md3-on-surface" />}
                                <Badge className="bg-md3-secondary-container text-md3-on-secondary-container">
                                  {activity.activity_type?.replace(/_/g, ' ')}
                                </Badge>
                                <span className="text-md3-body-medium text-md3-on-surface font-medium">
                                  {roundHrs(activity.allocated_hours)}hrs
                                </span>
                                {activity.has_variance_warning && (
                                  <AlertTriangle className="h-4 w-4 text-md3-error" />
                                )}
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="text-md3-body-small text-md3-on-surface-variant">
                                  {activity.tasks?.length || 0} tasks
                                </span>
                                <Progress
                                  value={activity.completion_percentage || 0}
                                  className="w-20"
                                />
                                {canModify && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedActivity(activity);
                                        setEditHours(activity.allocated_hours?.toString() || '');
                                        setEditActivityDialog(true);
                                      }}
                                      className="text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedActivity(activity);
                                        setDeleteTarget({ type: 'activity', item: activity });
                                        setDeleteConfirmDialog(true);
                                      }}
                                      className="text-md3-error hover:bg-md3-error-container rounded-md3-full"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedActivity(activity);
                                        setTaskDescription('');
                                        setTaskHours('');
                                        setTaskAssignedTo('');
                                        setTaskPlannedStartDate('');
                                        setTaskDueDate('');
                                        setAddTaskDialog(true);
                                      }}
                                      className="border-md3-outline text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                                      data-testid={`co-activity-add-task-${activity.id}`}
                                    >
                                      <Plus className="h-4 w-4 mr-1" />
                                      Add Task
                                    </Button>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Tasks inside CO Activity */}
                            {expandedActivities[activity.id] && (
                              <div className="border-t border-md3-outline-variant p-3 space-y-2 bg-md3-surface">
                                {activity.tasks?.length > 0 ? (
                                  activity.tasks.map(task => (
                                    <div
                                      key={task.id}
                                      className="flex items-center justify-between p-2 bg-md3-surface-container rounded-md3-sm"
                                    >
                                      <div className="flex-1">
                                        <p className="text-md3-body-medium text-md3-on-surface">{task.description}</p>
                                        <div className="flex items-center gap-3 mt-1 text-md3-body-small text-md3-on-surface-variant">
                                          <span>{roundHrs(task.allocated_hours)}hrs</span>
                                          {task.assigned_user_name && (
                                            <span className="flex items-center gap-1">
                                              <Users className="h-3 w-3" />
                                              {task.assigned_user_name}
                                            </span>
                                          )}
                                          {task.due_date && (
                                            <span className="flex items-center gap-1">
                                              <Clock className="h-3 w-3" />
                                              {task.due_date}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Badge
                                          className={
                                            task.status === 'done' ? 'bg-md3-tertiary-container text-md3-on-tertiary-container' :
                                            task.status === 'in_progress' ? 'bg-md3-primary-container text-md3-on-primary-container' :
                                            'bg-md3-surface-container-high text-md3-on-surface-variant'
                                          }
                                        >
                                          {task.status?.replace(/_/g, ' ') || 'todo'}
                                        </Badge>
                                        {canModify && (
                                          <>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              onClick={() => {
                                                setSelectedActivity(activity);
                                                setSelectedTask(task);
                                                setTaskDescription(task.description || '');
                                                setTaskHours(task.allocated_hours?.toString() || '');
                                                setTaskAssignedTo(task.assigned_to || 'unassigned');
                                                setTaskPlannedStartDate(task.planned_start_date || '');
                                                setTaskDueDate(task.due_date || '');
                                                setTaskStatus(task.status || 'todo');
                                                setEditTaskDialog(true);
                                              }}
                                              className="h-7 w-7 p-0 text-md3-primary hover:bg-md3-primary-container rounded-md3-full"
                                            >
                                              <Edit2 className="h-3 w-3" />
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              onClick={() => {
                                                setSelectedActivity(activity);
                                                setDeleteTarget({ type: 'task', item: task });
                                                setDeleteConfirmDialog(true);
                                              }}
                                              className="h-7 w-7 p-0 text-md3-error hover:bg-md3-error-container rounded-md3-full"
                                            >
                                              <Trash2 className="h-3 w-3" />
                                            </Button>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <p className="text-md3-body-small text-md3-on-surface-variant italic text-center py-2">
                                    No tasks yet. Click &quot;Add Task&quot; to create one.
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                        <p className={`text-md3-body-small font-medium ${remaining > 0 ? 'text-md3-primary' : remaining < 0 ? 'text-md3-error' : 'text-md3-on-surface-variant'}`}>
                          Remaining: {remaining}hrs
                        </p>
                      </>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}

          {/* Add Scope Dialog */}
          <Dialog open={addScopeDialog} onOpenChange={setAddScopeDialog}>
            <DialogContent className="bg-md3-surface-container-high rounded-md3-xl">
              <DialogHeader>
                <DialogTitle className="text-md3-title-large text-md3-on-surface">
                  Add Scope to {selectedPhase?.phase_type === 'approval' ? 'Approval' : 'Fabrication'} Phase
                </DialogTitle>
                <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
                  Define a new scope group for {selectedPhase?.phase_type || 'project'} planning
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Scope Name *</Label>
                  <Input
                    placeholder="e.g., Shop 1, Building A"
                    id="scopeName"
                    className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                  />
                </div>
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Allocated Hours *</Label>
                  <Input
                    type="number"
                    placeholder="Hours for this scope"
                    id="scopeHours"
                    className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                  />
                  {(() => {
                    const usedHours = selectedPhase?.scope_allocations?.reduce((sum, s) => sum + (s.allocated_hours || 0), 0) || 0;
                    const remainingHours = (selectedPhase?.allocated_hours || 0) - usedHours;
                    return (
                      <div className="mt-2 space-y-1">
                        <p className="text-md3-body-small text-md3-on-surface-variant">
                          Phase total: {roundHrs(selectedPhase?.allocated_hours)}hrs
                        </p>
                        <p className="text-md3-body-small text-md3-on-surface-variant">
                          Already allocated: {roundHrs(usedHours)}hrs
                        </p>
                        <p className={`text-md3-body-small font-medium ${remainingHours > 0 ? 'text-md3-primary' : 'text-md3-error'}`}>
                          Remaining: {roundHrs(remainingHours)}hrs
                        </p>
                      </div>
                    );
                  })()}
                </div>
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setAddScopeDialog(false)}
                  className="border-md3-outline text-md3-primary rounded-md3-full"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={async () => {
                    const name = document.getElementById('scopeName').value;
                    const hours = parseFloat(document.getElementById('scopeHours').value);
                    
                    if (!name || !hours) {
                      toast.error('Please fill all required fields');
                      return;
                    }
                    
                    // Validate hours limit
                    const usedHours = selectedPhase?.scope_allocations?.reduce((sum, s) => sum + (s.allocated_hours || 0), 0) || 0;
                    const remainingHours = (selectedPhase?.allocated_hours || 0) - usedHours;
                    
                    if (hours > remainingHours) {
                      toast.error(`Hours exceed limit! Only ${roundHrs(remainingHours)}hrs remaining in this phase`);
                      return;
                    }

                    try {
                      await api.post(`/planning/phases/${selectedPhase.id}/scopes`, {
                        scope_name: name,
                        allocated_hours: hours
                      });

                      toast.success('Scope added successfully');
                      setAddScopeDialog(false);
                      reloadData();
                      document.getElementById('scopeName').value = '';
                      document.getElementById('scopeHours').value = '';
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to add scope');
                    }
                  }}
                  className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
                >
                  Add Scope
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Add Activity to Change Order Dialog */}
          <Dialog open={addCoActivityDialog} onOpenChange={setAddCoActivityDialog}>
            <DialogContent className="bg-md3-surface-container-high rounded-md3-xl">
              <DialogHeader>
                <DialogTitle className="text-md3-title-large text-md3-on-surface">
                  Add Activity to Change Order {selectedChangeOrder?.change_order_number || ''}
                </DialogTitle>
                <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
                  Plan an activity under this approved change order.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Activity Type *</Label>
                  <Select value={coActivityType} onValueChange={setCoActivityType}>
                    <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm" data-testid="co-activity-type-select">
                      <SelectValue placeholder="Select activity type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="modeling">Modeling</SelectItem>
                      <SelectItem value="detailing">Detailing</SelectItem>
                      <SelectItem value="erection">Erection</SelectItem>
                      <SelectItem value="planning">Planning</SelectItem>
                      <SelectItem value="model_checking">Model Checking</SelectItem>
                      <SelectItem value="detail_checking">Detail Checking</SelectItem>
                      <SelectItem value="erection_checking">Erection Checking</SelectItem>
                      <SelectItem value="rfi">RFI Handling</SelectItem>
                      <SelectItem value="transmittal">Transmittal Preparation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Allocated Hours *</Label>
                  <Input
                    type="number"
                    placeholder="Hours for this activity"
                    value={coActivityHours}
                    onChange={(e) => setCoActivityHours(e.target.value)}
                    className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                    data-testid="co-activity-hours-input"
                  />
                  {selectedChangeOrder && (() => {
                    const total = roundHrs(getCoHours(selectedChangeOrder));
                    const used = roundHrs(getCoAllocatedHours(selectedChangeOrder.id));
                    const remaining = total - used;
                    return (
                      <div className="mt-2 space-y-1">
                        <p className="text-md3-body-small text-md3-on-surface-variant">CO total: {total}hrs</p>
                        <p className="text-md3-body-small text-md3-on-surface-variant">Already planned: {used}hrs</p>
                        <p className={`text-md3-body-small font-medium ${remaining > 0 ? 'text-md3-primary' : 'text-md3-error'}`}>
                          Remaining: {remaining}hrs
                        </p>
                      </div>
                    );
                  })()}
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setAddCoActivityDialog(false)}
                  className="border-md3-outline text-md3-primary rounded-md3-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (!selectedChangeOrder) return;
                    if (!coActivityType || !coActivityHours) {
                      toast.error('Please fill all required fields');
                      return;
                    }
                    const hours = parseFloat(coActivityHours);
                    if (Number.isNaN(hours) || hours <= 0) {
                      toast.error('Allocated hours must be greater than 0');
                      return;
                    }
                    try {
                      await api.post(`/planning/change-orders/${selectedChangeOrder.id}/activities`, {
                        project_id: projectId,
                        activity_type: coActivityType,
                        activity_name: coActivityType.replace(/_/g, ' '),
                        allocated_hours: hours,
                        activity_order: getActivitiesForChangeOrder(selectedChangeOrder.id).length
                      });
                      toast.success('Change order activity added');
                      setAddCoActivityDialog(false);
                      setExpandedChangeOrders(prev => ({ ...prev, [selectedChangeOrder.id]: true }));
                      reloadData();
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to add activity');
                    }
                  }}
                  className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
                  data-testid="co-activity-save-btn"
                >
                  Add Activity
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Add Activity Dialog - Simplified: just type and hours */}
          <Dialog open={addActivityDialog} onOpenChange={setAddActivityDialog}>
            <DialogContent className="bg-md3-surface-container-high rounded-md3-xl">
              <DialogHeader>
                <DialogTitle className="text-md3-title-large text-md3-on-surface">
                  Add Activity to {selectedScope?.scope_name}
                </DialogTitle>
                <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
                  Select activity type and allocate hours. You can add tasks inside the activity later.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Activity Type *</Label>
                  <Select onValueChange={(value) => {
                    document.getElementById('activityTypeHidden').value = value;
                  }}>
                    <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm">
                      <SelectValue placeholder="Select activity type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="modeling">Modeling</SelectItem>
                      <SelectItem value="detailing">Detailing</SelectItem>
                      <SelectItem value="erection">Erection</SelectItem>
                      <SelectItem value="planning">Planning</SelectItem>
                      <SelectItem value="model_checking">Model Checking</SelectItem>
                      <SelectItem value="detail_checking">Detail Checking</SelectItem>
                      <SelectItem value="erection_checking">Erection Checking</SelectItem>
                      <SelectItem value="rfi">RFI Handling</SelectItem>
                      <SelectItem value="transmittal">Transmittal Preparation</SelectItem>
                    </SelectContent>
                  </Select>
                  <input type="hidden" id="activityTypeHidden" />
                </div>
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Allocated Hours *</Label>
                  <Input
                    type="number"
                    id="activityHours"
                    placeholder="Hours for this activity"
                    className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                  />
                  <p className="text-md3-body-small text-md3-on-surface-variant mt-1">
                    Scope allocation: {roundHrs(selectedScope?.allocated_hours)}hrs
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setAddActivityDialog(false)}
                  className="border-md3-outline text-md3-primary rounded-md3-full"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={async () => {
                    const type = document.getElementById('activityTypeHidden').value;
                    const hours = parseFloat(document.getElementById('activityHours').value);
                    
                    if (!type || !hours) {
                      toast.error('Please select activity type and enter hours');
                      return;
                    }

                    try {
                      await api.post(`/planning/scopes/${selectedScope.id}/activities`, {
                        phase_id: selectedPhase.id,
                        activity_type: type,
                        activity_name: type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                        allocated_hours: hours,
                        activity_order: 0
                      });

                      toast.success('Activity added successfully');
                      setAddActivityDialog(false);
                      // Reload data
                      const [phasesRes, activitiesRes] = await Promise.all([
                        api.get(`/projects/${projectId}/planning/phases`),
                        api.get(`/projects/${projectId}/planning/activities`)
                      ]);
                      setPhases(phasesRes.data.phases || []);
                      setActivities(activitiesRes.data.activities || []);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to add activity');
                    }
                  }}
                  className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
                >
                  Add Activity
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Add Task Dialog - Kanban style task inside activity */}
          <Dialog open={addTaskDialog} onOpenChange={setAddTaskDialog}>
            <DialogContent className="bg-md3-surface-container-high rounded-md3-xl">
              <DialogHeader>
                <DialogTitle className="text-md3-title-large text-md3-on-surface">
                  Add Task to {selectedActivity?.activity_type?.replace(/_/g, ' ')}
                </DialogTitle>
                <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
                  Create a task with description, hours, assignment and dates
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Task Description *</Label>
                  <Input
                    value={taskDescription}
                    onChange={(e) => setTaskDescription(e.target.value)}
                    placeholder="e.g., Model main beams Level 1-5"
                    className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                    data-testid="planning-task-desc-input"
                  />
                </div>
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Hours *</Label>
                  <Input
                    type="number"
                    value={taskHours}
                    onChange={(e) => setTaskHours(e.target.value)}
                    placeholder="Hours for this task"
                    className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                    data-testid="planning-task-hours-input"
                  />
                  <p className="text-md3-body-small text-md3-on-surface-variant mt-1">
                    Activity allocation: {roundHrs(selectedActivity?.allocated_hours)}hrs
                  </p>
                </div>
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Assigned To</Label>
                  <Select value={taskAssignedTo} onValueChange={setTaskAssignedTo}>
                    <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm" data-testid="planning-task-assignee-select">
                      <SelectValue placeholder="Select team member (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {getEligibleAssignees(selectedActivity?.activity_type, false).map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} - {u.department}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {team.length > 0 && getEligibleAssignees(selectedActivity?.activity_type, false).length === 0 && (
                    <p className="text-xs text-md3-error mt-1" data-testid="planning-no-eligible-producer">
                      No project team member has the required role for this activity. Add one to the Project Team first.
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-md3-label-large text-md3-on-surface">Planned Start Date</Label>
                    <Input
                      type="date"
                      value={taskPlannedStartDate}
                      onChange={(e) => setTaskPlannedStartDate(e.target.value)}
                      className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                      data-testid="planning-task-start-date"
                    />
                    <p className="text-md3-body-small text-md3-on-surface-variant mt-1">
                      When work should begin
                    </p>
                  </div>
                  <div>
                    <Label className="text-md3-label-large text-md3-on-surface">Due Date</Label>
                    <Input
                      type="date"
                      value={taskDueDate}
                      onChange={(e) => setTaskDueDate(e.target.value)}
                      className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                      data-testid="planning-task-due-date"
                    />
                    <p className="text-md3-body-small text-md3-on-surface-variant mt-1">
                      When work must be completed
                    </p>
                  </div>
                </div>

                {/* Auto-pair Checking Task section (only for producer activities) */}
                {selectedActivity && ['modeling', 'detailing', 'erection'].includes((selectedActivity.activity_type || '').toLowerCase()) && (
                  <div className="border border-blue-200 bg-blue-50 rounded-md3-sm p-3 space-y-3">
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id="auto-pair-check"
                        checked={autoPairChecker}
                        onChange={(e) => setAutoPairChecker(e.target.checked)}
                        className="mt-1"
                        data-testid="planning-autopair-checkbox"
                      />
                      <label htmlFor="auto-pair-check" className="text-md3-label-large text-md3-on-surface cursor-pointer flex-1">
                        Auto-create paired checking task
                        <p className="text-md3-body-small text-md3-on-surface-variant mt-0.5">
                          A linked checking task will be created automatically. If a {selectedActivity.activity_type === 'modeling' ? 'Model Checking' : selectedActivity.activity_type === 'detailing' ? 'Detail Checking' : 'Erection Checking'} activity does not yet exist under this scope, it will be auto-created. The checker must approve to close this task.
                        </p>
                      </label>
                    </div>
                    {autoPairChecker && (
                      <div className="grid grid-cols-2 gap-3 pl-6">
                        <div>
                          <Label className="text-xs">Checker (optional)</Label>
                          <Select value={taskCheckerAssignedTo} onValueChange={setTaskCheckerAssignedTo}>
                            <SelectTrigger className="mt-1 bg-white text-xs h-9" data-testid="planning-checker-select">
                              <SelectValue placeholder="Assign later" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="unassigned">Unassigned</SelectItem>
                              {getEligibleAssignees(selectedActivity?.activity_type, true).map(u => (
                                <SelectItem key={u.id} value={u.id}>
                                  {u.first_name} {u.last_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {team.length > 0 && getEligibleAssignees(selectedActivity?.activity_type, true).length === 0 && (
                            <p className="text-[10px] text-md3-error mt-1" data-testid="planning-no-eligible-checker">
                              No team member has the Checker role. Add one to the Project Team first.
                            </p>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs">Check Hours</Label>
                          <Input
                            type="number"
                            step="0.5"
                            value={taskCheckerHours}
                            onChange={(e) => setTaskCheckerHours(e.target.value)}
                            placeholder={`auto: ${Math.max(0.5, Math.round((parseFloat(taskHours) || 0) * 0.2 * 10) / 10)}h`}
                            className="mt-1 bg-white text-xs h-9"
                            data-testid="planning-checker-hours"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setAddTaskDialog(false)}
                  className="border-md3-outline text-md3-primary rounded-md3-full"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={async () => {
                    if (!taskDescription || !taskHours) {
                      toast.error('Please enter task description and hours');
                      return;
                    }

                    try {
                      const body = {
                        description: taskDescription,
                        allocated_hours: parseFloat(taskHours),
                        assigned_to: taskAssignedTo === 'unassigned' ? null : (taskAssignedTo || null),
                        planned_start_date: taskPlannedStartDate || null,
                        due_date: taskDueDate || null,
                      };
                      // Producer-checker pairing
                      const isProducer = selectedActivity && ['modeling', 'detailing', 'erection'].includes((selectedActivity.activity_type || '').toLowerCase());
                      if (isProducer) {
                        body.auto_pair = autoPairChecker;
                        if (autoPairChecker) {
                          body.checker_assigned_to = taskCheckerAssignedTo === 'unassigned' ? null : (taskCheckerAssignedTo || null);
                          if (taskCheckerHours) body.checker_hours = parseFloat(taskCheckerHours);
                        }
                      }
                      const callAdd = async (overrideLeave) => {
                        const finalBody = overrideLeave ? { ...body, confirm_over_leave: true } : body;
                        return await api.post(`/planning/activities/${selectedActivity.id}/tasks`, finalBody);
                      };
                      let res;
                      try {
                        res = await callAdd(false);
                      } catch (err) {
                        const detail = err?.response?.data?.detail;
                        if (err?.response?.status === 409 && detail?.code === 'leave_conflict') {
                          // Show confirmation; on confirm retry with override
                          await new Promise((resolve) => {
                            setLeaveConflictDialog({
                              open: true,
                              leaves: detail.leaves || [],
                              onConfirm: async () => {
                                try {
                                  res = await callAdd(true);
                                  setLeaveConflictDialog({ open: false, leaves: [], onConfirm: null });
                                  resolve();
                                } catch (e2) {
                                  toast.error(e2.response?.data?.detail || 'Failed to add task');
                                  setLeaveConflictDialog({ open: false, leaves: [], onConfirm: null });
                                  resolve();
                                }
                              },
                              onCancel: () => {
                                setLeaveConflictDialog({ open: false, leaves: [], onConfirm: null });
                                resolve();
                              },
                            });
                          });
                          if (!res) return; // user cancelled
                        } else {
                          throw err;
                        }
                      }

                      toast.success(res.data?.message || 'Task added successfully');
                      setAddTaskDialog(false);
                      // Reset paired fields
                      setTaskCheckerAssignedTo('');
                      setTaskCheckerHours('');
                      // Reload activities
                      const activitiesRes = await api.get(`/projects/${projectId}/planning/activities`);
                      setActivities(activitiesRes.data.activities || []);
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to add task');
                    }
                  }}
                  className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
                  data-testid="planning-task-save-btn"
                >
                  Add Task
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit Scope Dialog */}
          <Dialog open={editScopeDialog} onOpenChange={setEditScopeDialog}>
            <DialogContent className="bg-md3-surface-container-high rounded-md3-xl">
              <DialogHeader>
                <DialogTitle className="text-md3-title-large text-md3-on-surface">Edit Scope</DialogTitle>
                <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
                  Rename and reallocate hours for {selectedScope?.scope_name}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Scope Name *</Label>
                  <Input
                    value={editScopeName}
                    onChange={(e) => setEditScopeName(e.target.value)}
                    placeholder="e.g. Structural, Miscellaneous"
                    className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                    data-testid="planning-scope-name-input"
                    maxLength={200}
                  />
                  <p className="text-md3-body-small text-md3-on-surface-variant mt-1">
                    Renaming also updates all activities under this scope.
                  </p>
                </div>
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Allocated Hours *</Label>
                  <Input
                    type="number"
                    value={editHours}
                    onChange={(e) => setEditHours(e.target.value)}
                    className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                    data-testid="planning-scope-hours-input"
                  />
                  <p className="text-md3-body-small text-md3-on-surface-variant mt-1">
                    Phase total: {roundHrs(selectedPhase?.allocated_hours)}hrs
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setEditScopeDialog(false)}
                  className="border-md3-outline text-md3-primary rounded-md3-full"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={async () => {
                    if (!editHours) {
                      toast.error('Please enter valid hours');
                      return;
                    }
                    const cleanedName = (editScopeName || '').trim();
                    if (!cleanedName) {
                      toast.error('Scope name cannot be empty');
                      return;
                    }

                    try {
                      const res = await api.put(`/planning/scopes/${selectedScope.id}`, {
                        phase_id: selectedPhase.id,
                        allocated_hours: parseFloat(editHours),
                        scope_name: cleanedName,
                      });
                      const activitiesTouched = res.data?.activities_updated || 0;
                      const nameChanged = !!res.data?.name_changed;
                      toast.success(
                        nameChanged && activitiesTouched > 0
                          ? `Scope updated — renamed on ${activitiesTouched} activit${activitiesTouched === 1 ? 'y' : 'ies'}`
                          : 'Scope updated successfully'
                      );
                      setEditScopeDialog(false);
                      reloadData();
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to update scope');
                    }
                  }}
                  className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
                  data-testid="planning-scope-save-btn"
                >
                  Save Changes
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit Activity Dialog */}
          <Dialog open={editActivityDialog} onOpenChange={setEditActivityDialog}>
            <DialogContent className="bg-md3-surface-container-high rounded-md3-xl">
              <DialogHeader>
                <DialogTitle className="text-md3-title-large text-md3-on-surface">Edit Activity</DialogTitle>
                <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
                  Update hours for {selectedActivity?.activity_type?.replace(/_/g, ' ')}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Allocated Hours *</Label>
                  <Input
                    type="number"
                    value={editHours}
                    onChange={(e) => setEditHours(e.target.value)}
                    className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setEditActivityDialog(false)}
                  className="border-md3-outline text-md3-primary rounded-md3-full"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={async () => {
                    if (!editHours) {
                      toast.error('Please enter valid hours');
                      return;
                    }
                    try {
                      await api.put(`/planning/activities/${selectedActivity.id}`, {
                        allocated_hours: parseFloat(editHours)
                      });
                      toast.success('Activity updated successfully');
                      setEditActivityDialog(false);
                      reloadData();
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to update activity');
                    }
                  }}
                  className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
                >
                  Update
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit Task Dialog */}
          <Dialog open={editTaskDialog} onOpenChange={setEditTaskDialog}>
            <DialogContent className="bg-md3-surface-container-high rounded-md3-xl">
              <DialogHeader>
                <DialogTitle className="text-md3-title-large text-md3-on-surface">Edit Task</DialogTitle>
                <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
                  Update task details
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Description *</Label>
                  <Input
                    value={taskDescription}
                    onChange={(e) => setTaskDescription(e.target.value)}
                    className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                  />
                </div>
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Hours *</Label>
                  <Input
                    type="number"
                    value={taskHours}
                    onChange={(e) => setTaskHours(e.target.value)}
                    className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                  />
                </div>
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Status</Label>
                  <Select value={taskStatus} onValueChange={setTaskStatus}>
                    <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todo">To Do</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="done">Done</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-md3-label-large text-md3-on-surface">Assigned To</Label>
                  <Select value={taskAssignedTo} onValueChange={setTaskAssignedTo}>
                    <SelectTrigger className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {getEligibleAssignees(selectedActivity?.activity_type, false).map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.first_name} {u.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-md3-label-large text-md3-on-surface">Planned Start Date</Label>
                    <Input
                      type="date"
                      value={taskPlannedStartDate}
                      onChange={(e) => setTaskPlannedStartDate(e.target.value)}
                      className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-md3-label-large text-md3-on-surface">Due Date</Label>
                    <Input
                      type="date"
                      value={taskDueDate}
                      onChange={(e) => setTaskDueDate(e.target.value)}
                      className="mt-1 bg-md3-surface border-md3-outline rounded-md3-sm"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setEditTaskDialog(false)}
                  className="border-md3-outline text-md3-primary rounded-md3-full"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={async () => {
                    if (!taskDescription || !taskHours) {
                      toast.error('Please fill required fields');
                      return;
                    }
                    try {
                      const body = {
                        activity_id: selectedActivity.id,
                        description: taskDescription,
                        allocated_hours: parseFloat(taskHours),
                        assigned_to: taskAssignedTo === 'unassigned' ? null : taskAssignedTo,
                        planned_start_date: taskPlannedStartDate || null,
                        due_date: taskDueDate || null,
                        status: taskStatus
                      };
                      const callUpd = async (overrideLeave) => {
                        const finalBody = overrideLeave ? { ...body, confirm_over_leave: true } : body;
                        return await api.put(`/planning/tasks/${selectedTask.id}`, finalBody);
                      };
                      try {
                        await callUpd(false);
                      } catch (err) {
                        const detail = err?.response?.data?.detail;
                        if (err?.response?.status === 409 && detail?.code === 'leave_conflict') {
                          let proceeded = false;
                          await new Promise((resolve) => {
                            setLeaveConflictDialog({
                              open: true,
                              leaves: detail.leaves || [],
                              onConfirm: async () => {
                                try {
                                  await callUpd(true);
                                  proceeded = true;
                                } catch (e2) {
                                  toast.error(e2.response?.data?.detail || 'Failed to update task');
                                }
                                setLeaveConflictDialog({ open: false, leaves: [], onConfirm: null });
                                resolve();
                              },
                              onCancel: () => {
                                setLeaveConflictDialog({ open: false, leaves: [], onConfirm: null });
                                resolve();
                              },
                            });
                          });
                          if (!proceeded) return;
                        } else {
                          throw err;
                        }
                      }
                      toast.success('Task updated successfully');
                      setEditTaskDialog(false);
                      reloadData();
                    } catch (error) {
                      toast.error(error.response?.data?.detail || 'Failed to update task');
                    }
                  }}
                  className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
                >
                  Update
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteConfirmDialog} onOpenChange={setDeleteConfirmDialog}>
            <DialogContent className="bg-md3-surface-container-high rounded-md3-xl">
              <DialogHeader>
                <DialogTitle className="text-md3-title-large text-md3-error">Confirm Delete</DialogTitle>
                <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
                  Are you sure you want to delete this {deleteTarget.type}? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <p className="text-md3-body-medium text-md3-on-surface">
                  {deleteTarget.type === 'scope' && `Scope: ${deleteTarget.item?.scope_name}`}
                  {deleteTarget.type === 'activity' && `Activity: ${deleteTarget.item?.activity_type?.replace(/_/g, ' ')} (${roundHrs(deleteTarget.item?.allocated_hours)}hrs)`}
                  {deleteTarget.type === 'task' && `Task: ${deleteTarget.item?.description}`}
                  {deleteTarget.type === 'phase' && `Phase: ${deleteTarget.item?.phase_type}`}
                </p>
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => setDeleteConfirmDialog(false)}
                  className="border-md3-outline text-md3-primary rounded-md3-full"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleDelete}
                  className="bg-md3-error hover:bg-md3-error/90 text-md3-on-error rounded-md3-full"
                >
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Leave Conflict Confirmation Dialog */}
          <Dialog
            open={leaveConflictDialog.open}
            onOpenChange={(open) => {
              if (!open) leaveConflictDialog.onCancel?.();
              setLeaveConflictDialog((d) => ({ ...d, open }));
            }}
          >
            <DialogContent className="bg-md3-surface-container-high rounded-md3-xl max-w-lg" data-testid="planning-leave-conflict-dialog">
              <DialogHeader>
                <DialogTitle className="text-md3-title-large text-amber-700">Approved Leave Conflict</DialogTitle>
                <DialogDescription className="text-md3-body-medium text-md3-on-surface-variant">
                  The assignee has approved leave overlapping these dates. You can still proceed if you've coordinated with the team.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 py-2 text-sm">
                {(leaveConflictDialog.leaves || []).map((lv) => (
                  <div key={lv.application_id} className="bg-md3-surface-container-low rounded p-2">
                    <div className="font-medium text-md3-on-surface">
                      {lv.leave_type || "Leave"}
                      {lv.application_number ? ` · ${lv.application_number}` : ""}
                      {lv.is_half_day ? ` · Half day (${lv.half_day_period || ""})` : ""}
                    </div>
                    <div className="text-xs text-md3-on-surface-variant">
                      {lv.start_date} → {lv.end_date}
                    </div>
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => leaveConflictDialog.onCancel?.()}
                  className="border-md3-outline text-md3-primary rounded-md3-full"
                  data-testid="planning-leave-conflict-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => leaveConflictDialog.onConfirm?.()}
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-md3-full"
                  data-testid="planning-leave-conflict-confirm"
                >
                  Proceed anyway
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </Layout>
  );
};

export default ProjectPlanning;
